'use client';

import { UserType } from '@baseball-near-you/common';
import { Button, cn } from '@baseball-near-you/ui';
import { PATHS, Setting } from '@bb/lib/constants';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';

enum NextStepStatus {
  Completed = 'Completed',
  InProgress = 'In Progress',
  NotStarted = 'Not Started',
}

export function NextStepsStep(props: {
  onStepSuccess: (href?: string) => void;
}) {
  const { userProfile } = useUserProfile();

  const personalDetails = useMemo(
    () => [
      userProfile?.firstName,
      userProfile?.lastName,
      userProfile?.birthday,
      userProfile?.phoneNumber,
      userProfile?.batHandedness,
      userProfile?.throwHandedness,
      (userProfile?.positions ?? []).length > 0,
    ],
    [userProfile]
  );

  const personalNextSteps = useMemo(
    () => [
      // TODO not verifying email bc supabase doesnt support OTPs without signing out the user first?
      // - https://github.com/supabase/supabase-js/issues/991
      // {
      //   title: 'Verify your account',
      //   status: userProfile?.emailVerified
      //     ? NextStepStatus.Completed
      //     : NextStepStatus.NotStarted,
      //   href: PATHS.Settings + Setting.Profile,
      //   disabled: false,
      // },
      {
        title: 'Enter player details',
        status: personalDetails.every((val) => !!val)
          ? NextStepStatus.Completed
          : personalDetails.some((val) => !!val)
            ? NextStepStatus.InProgress
            : NextStepStatus.NotStarted,
        href: PATHS.Settings + Setting.Profile,
        disabled: false,
      },
      {
        title: 'Review a travel team',
        status:
          (userProfile?.Review ?? []).length > 0
            ? NextStepStatus.Completed
            : NextStepStatus.NotStarted,
        href: PATHS.Teams,
        disabled: false,
      },
    ],
    [userProfile]
  );

  const organizationNextSteps = useMemo(
    () => [
      // TODO not verifying email bc supabase doesnt support OTPs without signing out the user first?
      // - https://github.com/supabase/supabase-js/issues/991
      // {
      //   title: 'Verify your account',
      //   status: userProfile?.emailVerified
      //     ? NextStepStatus.Completed
      //     : NextStepStatus.NotStarted,
      //   href: PATHS.Settings + Setting.Profile,
      //   disabled: false,
      // },
      {
        title: 'Add or claim your team',
        status:
          userProfile && userProfile.UserTeam.length > 0
            ? NextStepStatus.Completed
            : (userProfile?.claims ?? []).length > 0
              ? NextStepStatus.InProgress
              : NextStepStatus.NotStarted,
        href: PATHS.Settings + Setting.Team,
        disabled: false,
      },
      {
        title: 'Add organization details',
        status: [
          userProfile?.UserTeam?.[0]?.team?.recruiting,
          userProfile?.UserTeam?.[0]?.team?.tournaments,
          userProfile?.UserTeam?.[0]?.team?.tryouts,
          userProfile?.UserTeam?.[0]?.team?.travel,
          userProfile?.UserTeam?.[0]?.team?.training,
        ].some((val) => !!val)
          ? NextStepStatus.Completed
          : NextStepStatus.NotStarted,
        href: PATHS.Settings + Setting.Team + Setting.TeamDetails,
        disabled: !userProfile?.UserTeam?.[0]?.team?.id,
      },
    ],
    [userProfile]
  );

  const nextSteps = useMemo(() => {
    if (userProfile?.type === UserType.Personal) {
      return personalNextSteps;
    }
    return organizationNextSteps;
  }, [userProfile]);

  return (
    <div className="flex w-full flex-col gap-4">
      {nextSteps.map((step) => (
        <button
          type="button"
          key={step.title}
          className={cn(
            'flex w-full flex-col items-center justify-between rounded-md border px-4 py-5 shadow-none transition-all hover:scale-[101%] hover:shadow-xs sm:flex-row',
            step.disabled && 'pointer-events-none bg-gray-200 text-gray-700'
          )}
          disabled={step.disabled}
          onClick={async () => {
            await props.onStepSuccess(step.disabled ? undefined : step.href);
          }}
        >
          <h3 className="mb-2 font-semibold text-lg sm:mb-0">{step.title}</h3>
          <div className="flex items-center gap-2">
            <NextStepStatusPill status={step.status} disabled={step.disabled} />
            <ChevronRight />
          </div>
        </button>
      ))}
      <Button className="ml-auto bg-red" onClick={() => props.onStepSuccess()}>
        Complete onboarding
      </Button>
    </div>
  );
}

function NextStepStatusPill({
  status,
  disabled,
}: { status: NextStepStatus; disabled: boolean }) {
  const color = disabled
    ? 'text-gray-700 bg-gray-300 border-gray-400'
    : status === NextStepStatus.Completed
      ? 'text-green-700 bg-green-50 border-green-200'
      : status === NextStepStatus.InProgress
        ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
        : status === NextStepStatus.NotStarted
          ? 'text-gray-700 bg-gray-50 border-gray-200'
          : null;

  return (
    <p
      className={cn(
        'title rounded-full border px-12 py-1 text-base sm:px-2 sm:text-xs',
        color
      )}
    >
      {disabled ? 'blocked' : status}
    </p>
  );
}
